import { gql } from '@apollo/client';

// ==================== FRAGMENTS ====================

export const INVOICE_LINE_ITEM_FIELDS = gql`
  fragment InvoiceLineItemFields on InvoiceLineItem {
    id
    invoiceId
    productId
    sku
    description
    unitPrice
    quantity
    lineTotal
    sortOrder
    createdAt
    updatedAt
  }
`;

export const INVOICE_PAYMENT_FIELDS = gql`
  fragment InvoicePaymentFields on InvoicePayment {
    id
    invoiceId
    amount
    paymentDate
    paymentMethod
    referenceNumber
    notes
    createdBy
    createdAt
    updatedAt
  }
`;

export const INVOICE_FIELDS = gql`
  ${INVOICE_LINE_ITEM_FIELDS}
  ${INVOICE_PAYMENT_FIELDS}
  fragment InvoiceFields on Invoice {
    id
    invoiceNumber
    organizationId
    statementId
    status
    source
    externalRef
    invoiceDate
    dueDate
    subtotal
    total
    amountPaid
    balanceDue
    isRecurring
    recurringSchedule
    recurringStartDate
    recurringEndDate
    notes
    createdBy
    lineItems {
      ...InvoiceLineItemFields
    }
    payments {
      ...InvoicePaymentFields
    }
    createdAt
    updatedAt
  }
`;

export const INVOICE_SUMMARY_FIELDS = gql`
  fragment InvoiceSummaryFields on Invoice {
    id
    invoiceNumber
    organizationId
    statementId
    status
    source
    externalRef
    invoiceDate
    dueDate
    total
    amountPaid
    balanceDue
    createdBy
    createdAt
    updatedAt
  }
`;

export const STATEMENT_FIELDS = gql`
  ${INVOICE_FIELDS}
  fragment StatementFields on Statement {
    id
    statementNumber
    organizationId
    status
    billingPeriodStart
    billingPeriodEnd
    statementDate
    dueDate
    totalAmount
    amountPaid
    balanceDue
    notes
    createdBy
    invoices {
      ...InvoiceFields
    }
    createdAt
    updatedAt
  }
`;

export const STATEMENT_SUMMARY_FIELDS = gql`
  fragment StatementSummaryFields on Statement {
    id
    statementNumber
    organizationId
    status
    billingPeriodStart
    billingPeriodEnd
    statementDate
    dueDate
    totalAmount
    amountPaid
    balanceDue
    notes
    createdBy
    createdAt
    updatedAt
  }
`;

// ==================== QUERIES ====================

export const GET_INVOICE = gql`
  ${INVOICE_FIELDS}
  query GetInvoice($id: ID!) {
    invoice(id: $id) {
      ...InvoiceFields
    }
  }
`;

export const GET_INVOICES = gql`
  ${INVOICE_SUMMARY_FIELDS}
  query GetInvoices(
    $organizationId: ID
    $status: InvoiceStatus
    $source: InvoiceSource
    $search: String
    $dateFrom: String
    $dateTo: String
    $page: Int
    $limit: Int
  ) {
    invoices(
      organizationId: $organizationId
      status: $status
      source: $source
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      page: $page
      limit: $limit
    ) {
      invoices {
        ...InvoiceSummaryFields
      }
      total
    }
  }
`;

export const GET_INVOICES_BY_ORGANIZATION = gql`
  ${INVOICE_SUMMARY_FIELDS}
  query GetInvoicesByOrganization($organizationId: ID!) {
    invoicesByOrganization(organizationId: $organizationId) {
      ...InvoiceSummaryFields
    }
  }
`;

export const GET_STATEMENT = gql`
  ${STATEMENT_FIELDS}
  query GetStatement($id: ID!) {
    statement(id: $id) {
      ...StatementFields
    }
  }
`;

export const GET_STATEMENTS_BY_ORGANIZATION = gql`
  ${STATEMENT_SUMMARY_FIELDS}
  query GetStatementsByOrganization($organizationId: ID!) {
    statementsByOrganization(organizationId: $organizationId) {
      ...StatementSummaryFields
    }
  }
`;

export const GET_INVOICE_PAYMENTS = gql`
  ${INVOICE_PAYMENT_FIELDS}
  query GetInvoicePayments($invoiceId: ID!) {
    invoicePayments(invoiceId: $invoiceId) {
      ...InvoicePaymentFields
    }
  }
`;

// ==================== MUTATIONS ====================

export const CREATE_INVOICE = gql`
  ${INVOICE_FIELDS}
  mutation CreateInvoice($input: CreateInvoiceInput!) {
    createInvoice(input: $input) {
      ...InvoiceFields
    }
  }
`;

export const UPDATE_INVOICE = gql`
  ${INVOICE_FIELDS}
  mutation UpdateInvoice($id: ID!, $input: UpdateInvoiceInput!) {
    updateInvoice(id: $id, input: $input) {
      ...InvoiceFields
    }
  }
`;

export const ADD_INVOICE_LINE_ITEM = gql`
  ${INVOICE_FIELDS}
  mutation AddInvoiceLineItem($input: AddInvoiceLineItemInput!) {
    addInvoiceLineItem(input: $input) {
      ...InvoiceFields
    }
  }
`;

export const REMOVE_INVOICE_LINE_ITEM = gql`
  ${INVOICE_FIELDS}
  mutation RemoveInvoiceLineItem($invoiceId: ID!, $lineItemId: ID!) {
    removeInvoiceLineItem(invoiceId: $invoiceId, lineItemId: $lineItemId) {
      ...InvoiceFields
    }
  }
`;

export const APPROVE_INVOICE = gql`
  ${INVOICE_FIELDS}
  mutation ApproveInvoice($id: ID!) {
    approveInvoice(id: $id) {
      ...InvoiceFields
    }
  }
`;

export const VOID_INVOICE = gql`
  ${INVOICE_FIELDS}
  mutation VoidInvoice($id: ID!) {
    voidInvoice(id: $id) {
      ...InvoiceFields
    }
  }
`;

export const DISPUTE_INVOICE = gql`
  ${INVOICE_FIELDS}
  mutation DisputeInvoice($id: ID!) {
    disputeInvoice(id: $id) {
      ...InvoiceFields
    }
  }
`;

export const SEND_INVOICE_EMAIL = gql`
  mutation SendInvoiceEmail($id: ID!) {
    sendInvoiceEmail(id: $id) {
      success
      recipientEmail
      error
    }
  }
`;

export const SEND_STATEMENT_EMAIL = gql`
  mutation SendStatementEmail($id: ID!) {
    sendStatementEmail(id: $id) {
      success
      recipientEmail
      error
    }
  }
`;

export const DELETE_INVOICE = gql`
  mutation DeleteInvoice($id: ID!) {
    deleteInvoice(id: $id)
  }
`;

export const GENERATE_STATEMENT = gql`
  ${STATEMENT_FIELDS}
  mutation GenerateStatement($input: GenerateStatementInput!) {
    generateStatement(input: $input) {
      ...StatementFields
    }
  }
`;

export const FINALIZE_STATEMENT = gql`
  ${STATEMENT_FIELDS}
  mutation FinalizeStatement($id: ID!) {
    finalizeStatement(id: $id) {
      ...StatementFields
    }
  }
`;

export const VOID_STATEMENT = gql`
  ${STATEMENT_FIELDS}
  mutation VoidStatement($id: ID!) {
    voidStatement(id: $id) {
      ...StatementFields
    }
  }
`;

export const DELETE_STATEMENT = gql`
  mutation DeleteStatement($id: ID!) {
    deleteStatement(id: $id)
  }
`;

export const RECORD_PAYMENT = gql`
  ${INVOICE_PAYMENT_FIELDS}
  mutation RecordPayment($input: RecordPaymentInput!) {
    recordPayment(input: $input) {
      ...InvoicePaymentFields
    }
  }
`;

export const UPDATE_PAYMENT = gql`
  ${INVOICE_PAYMENT_FIELDS}
  mutation UpdatePayment($id: ID!, $input: UpdatePaymentInput!) {
    updatePayment(id: $id, input: $input) {
      ...InvoicePaymentFields
    }
  }
`;

export const DELETE_PAYMENT = gql`
  mutation DeletePayment($id: ID!) {
    deletePayment(id: $id)
  }
`;

// ==================== TYPES ====================

export type InvoiceStatus = 'DRAFT' | 'APPROVED' | 'SENT' | 'PAID' | 'PARTIAL' | 'DISPUTED' | 'VOID';
export type InvoiceSource = 'MANUAL' | 'BLAST' | 'RECURRING';
export type RecurringSchedule = 'MONTHLY' | 'QUARTERLY' | 'ANNUALLY';
export type StatementStatus = 'DRAFT' | 'FINALIZED' | 'SENT' | 'PAID' | 'PARTIAL' | 'VOID';
export type PaymentMethod = 'ACH' | 'WIRE' | 'CHECK' | 'OTHER';

export interface InvoiceLineItem {
  id: string;
  invoiceId: string;
  productId: string;
  sku: string;
  description: string;
  unitPrice: number;
  quantity: number;
  lineTotal: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface InvoicePayment {
  id: string;
  invoiceId: string;
  amount: number;
  paymentDate: string;
  paymentMethod: PaymentMethod;
  referenceNumber: string | null;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Invoice {
  id: string;
  invoiceNumber: string;
  organizationId: string;
  statementId: string | null;
  status: InvoiceStatus;
  source: InvoiceSource;
  externalRef: string | null;
  invoiceDate: string;
  dueDate: string;
  subtotal: number;
  total: number;
  amountPaid: number;
  balanceDue: number;
  isRecurring: boolean;
  recurringSchedule: RecurringSchedule | null;
  recurringStartDate: string | null;
  recurringEndDate: string | null;
  notes: string | null;
  createdBy: string | null;
  lineItems: InvoiceLineItem[];
  payments: InvoicePayment[];
  createdAt: string;
  updatedAt: string;
}

export interface InvoiceSummary {
  id: string;
  invoiceNumber: string;
  organizationId: string;
  statementId: string | null;
  status: InvoiceStatus;
  source: InvoiceSource;
  externalRef: string | null;
  invoiceDate: string;
  dueDate: string;
  total: number;
  amountPaid: number;
  balanceDue: number;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface Statement {
  id: string;
  statementNumber: string;
  organizationId: string;
  status: StatementStatus;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  statementDate: string;
  dueDate: string;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  notes: string | null;
  createdBy: string | null;
  invoices: Invoice[];
  createdAt: string;
  updatedAt: string;
}

export interface StatementSummary {
  id: string;
  statementNumber: string;
  organizationId: string;
  status: StatementStatus;
  billingPeriodStart: string;
  billingPeriodEnd: string;
  statementDate: string;
  dueDate: string;
  totalAmount: number;
  amountPaid: number;
  balanceDue: number;
  notes: string | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

// Query response types
export interface GetInvoiceResponse {
  invoice: Invoice | null;
}

export interface GetInvoicesResponse {
  invoices: {
    invoices: InvoiceSummary[];
    total: number;
  };
}

export interface GetInvoicesByOrganizationResponse {
  invoicesByOrganization: InvoiceSummary[];
}

export interface GetStatementResponse {
  statement: Statement | null;
}

export interface GetStatementsByOrganizationResponse {
  statementsByOrganization: StatementSummary[];
}

export interface GetInvoicePaymentsResponse {
  invoicePayments: InvoicePayment[];
}
